import {ChipTotalTaskSet, taskSetChipTotal} from './taskSetChipTotal';

/** The set fields the modal chip reads. Structural, like CountableTaskSet. */
export interface ModalChipTaskSet extends ChipTotalTaskSet {
	/** The Relay global id. The colour hash is a pure function of this string. */
	readonly id?: string | null;
	readonly name?: string | null;
}

export interface ModalChipInput {
	readonly featureFlagOn: boolean;
	readonly taskSet?: ModalChipTaskSet | null;
	readonly sequence?: number | null;
}

export interface ModalChipModel {
	readonly taskSetId: string;
	readonly name: string | null;
	readonly sequence: number | null;
	readonly total: number | null;
	readonly showPosition: boolean;
}

/**
 * Whether the task modal shows a task-set chip, and what it says.
 *
 * The denominator comes from taskSetChipTotal, the same function the scoping
 * chip and the timeline bar use -- not a copy of it. A date-bounded set shows
 * "3 of 14" on those surfaces, so the modal opened from one of their rows must
 * not say something else.
 *
 * Returns null for an ordinary task and with the flag off. Partial project
 * access is covered by the same null arm: TaskType.js withholds the whole
 * `taskSet` object, and `taskSetSequence` with it, without fullAccessToProject.
 */
export const taskSetModalChipModel = ({featureFlagOn, taskSet, sequence}: ModalChipInput): ModalChipModel | null => {
	if (!featureFlagOn) {
		return null;
	}
	if (!taskSet || !taskSet.id) {
		return null;
	}

	const total = taskSetChipTotal(taskSet);
	const position = sequence != null ? sequence : null;

	// A set whose instances were deleted can leave a sequence past the total.
	// "5 of 2" is worse than no position at all, which is what AC8 asks for.
	const showPosition = position != null && total != null && position >= 1 && position <= total;

	return {
		taskSetId: taskSet.id,
		name: taskSet.name != null ? taskSet.name : null,
		sequence: position,
		total,
		showPosition,
	};
};
